import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { CATEGORIES } from "../data/services";
import { useBookingLink } from "../lib/useBookingLinks";
import { pageForService } from "../data/treatment-pages";
import { Link } from "react-router-dom";
import { BOOKING_URL, CHERRY_URL, IMG } from "../data/site";
import { Reveal } from "../components/Reveal";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const ServiceRow = ({ s, cat }) => {
  const book = useBookingLink(s.name);
  return (
  <AccordionItem value={s.name} className="border-neutral-200" data-testid={`service-${cat}-${slug(s.name)}`}>
    <AccordionTrigger className="gap-6 py-6 text-left hover:no-underline">
      <span className="font-display flex-1 text-xl font-normal text-neutral-950 md:text-2xl">{s.name}</span>
      <span className="shrink-0 text-xs uppercase tracking-[0.18em] text-neutral-600" data-testid="service-price">{s.price}</span>
    </AccordionTrigger>
    <AccordionContent className="pb-8">
      <p className="max-w-2xl text-sm font-light leading-relaxed text-neutral-600 md:text-base">{s.desc}</p>
      {s.details && (
        <ul className="mt-4 grid max-w-2xl gap-2 text-sm font-light text-neutral-700 sm:grid-cols-2">
          {s.details.map((d) => <li key={d} className="border-l border-neutral-300 pl-3">{d}</li>)}
        </ul>
      )}
      <div className="mt-6 flex flex-wrap items-center gap-6">
        {pageForService(s.name)?.contactOnly
          ? <Link to="/contact-us" className="btn-ink px-6 py-3" data-testid={`contact-${slug(s.name)}`}>Contact Us to Schedule</Link>
          : <a href={book} target="_blank" rel="noreferrer" className="btn-ink px-6 py-3" data-testid={`book-${slug(s.name)}`}>Book This Treatment</a>}
        {pageForService(s.name) && <Link to={`/treatments/${pageForService(s.name).slug}`} className="link-under" data-testid={`learn-${slug(s.name)}`}>Learn More</Link>}
      </div>
    </AccordionContent>
  </AccordionItem>
  );
};

const CategoryBlock = ({ c, i }) => (
  <section id={c.id} className="scroll-mt-[220px] lg:scroll-mt-[280px] border-t border-neutral-200 py-20 md:py-28" data-testid={`category-${c.id}`}>
    <div className={`grid gap-12 lg:grid-cols-[1fr_1.6fr] lg:gap-20 ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}>
      <Reveal className="lg:sticky lg:top-[290px] lg:self-start">
        <div className="aspect-[4/5] overflow-hidden"><img src={c.image} alt={c.name} className="h-full w-full object-cover" loading="lazy" /></div>
        <p className="kicker mt-6">{c.kicker}</p>
        <h2 className="font-display mt-2 text-4xl font-light text-neutral-950 md:text-5xl">{c.name}</h2>
        <p className="mt-3 text-sm font-light leading-relaxed text-neutral-600">{c.tagline}</p>
      </Reveal>
      <Accordion type="single" collapsible className="w-full">
        {c.services.map((s) => <ServiceRow key={s.name} s={s} cat={c.id} />)}
      </Accordion>
    </div>
  </section>
);

export default function Treatments() {
  return (
    <main className="overflow-x-clip" data-testid="treatments-page">
      <section className="relative h-[52vh] min-h-[380px] overflow-hidden bg-neutral-900">
        <img src={IMG.peel} alt="Lux Veritae treatment suite" className="slowzoom absolute inset-0 h-full w-full object-cover grayscale-[30%]" />
        <div className="absolute inset-0 bg-black/45" />
        <div className="relative flex h-full flex-col items-center justify-center px-6 text-center text-white">
          <p className="rise text-[11px] uppercase tracking-[0.4em] text-neutral-200">The Menu</p>
          <h1 className="rise d1 font-display mt-4 text-5xl font-light sm:text-6xl lg:text-7xl">Treatments</h1>
          <p className="rise d2 mt-6 max-w-lg text-sm font-light text-neutral-200">Every treatment begins with a plan tailored to your skin and goals. Packages and Cherry financing available.</p>
        </div>
      </section>
      <nav className="sticky top-[144px] md:top-[160px] lg:top-[201px] z-30 border-b border-neutral-200 bg-white/95 backdrop-blur" data-testid="category-tabs">
        <div className="no-scrollbar mx-auto flex max-w-[1400px] gap-10 overflow-x-auto px-4 py-5 md:justify-center md:px-8">
          {CATEGORIES.map((c) => (
            <a key={c.id} href={`#${c.id}`} className="shrink-0 text-[11px] uppercase tracking-[0.22em] text-neutral-600 transition-colors hover:text-neutral-950" data-testid={`tab-${c.id}`}>{c.name}</a>
          ))}
        </div>
      </nav>
      <div className="mx-auto max-w-[1400px] px-4 md:px-8">
        {CATEGORIES.map((c, i) => <CategoryBlock key={c.id} c={c} i={i} />)}
      </div>
      <section className="bg-neutral-950 py-20 text-center text-white" data-testid="treatments-cta">
        <h2 className="font-display text-4xl font-light md:text-5xl">Ready when you are.</h2>
        <p className="mx-auto mt-4 max-w-md text-sm font-light text-neutral-400">Prices shown are our menu and “starting at” prices. Your final plan and quote are confirmed at consultation.</p>
        <div className="mt-10 flex flex-col justify-center gap-4 sm:flex-row">
          <a href={BOOKING_URL} target="_blank" rel="noreferrer" className="bg-white px-8 py-4 text-[11px] font-medium uppercase tracking-[0.2em] text-neutral-950 transition-colors hover:bg-neutral-200" data-testid="treatments-book-btn">Book Now</a>
          <a href={CHERRY_URL} target="_blank" rel="noreferrer" className="btn-ghost-light" data-testid="treatments-cherry-btn">Finance with Cherry</a>
        </div>
      </section>
    </main>
  );
}
